import readWorkbook, { readSheet } from 'read-excel-file/browser'
import type {
  Client,
  ImportField,
  ImportMapping,
  ParsedTable,
} from '../types'
import {
  isOsintWorkbook,
  normalizeTaxId,
  parseOsintSheets,
  type WorkbookSheet,
} from './catalog'

const MAX_IMPORT_BYTES = 10 * 1024 * 1024
const MAX_IMPORT_ROWS = 10_000
const MAX_IMPORT_COLUMNS = 100
const MAX_CELL_LENGTH = 50_000

export const IMPORT_FIELDS: Array<{ value: ImportField; label: string; required?: boolean }> = [
  { value: 'company', label: 'Компания', required: true },
  { value: 'taxId', label: 'ИНН' },
  { value: 'contactPerson', label: 'Контактное лицо' },
  { value: 'phone', label: 'Телефон' },
  { value: 'email', label: 'E-mail' },
  { value: 'city', label: 'Город' },
  { value: 'segment', label: 'Сегмент' },
  { value: 'potential', label: 'Потенциал / сумма' },
  { value: 'notes', label: 'Комментарий' },
]

const SYNONYMS: Record<ImportField, string[]> = {
  company: [
    'компания',
    'клиент',
    'организация',
    'наименование',
    'название',
    'контрагент',
    'company',
    'client',
  ],
  taxId: ['инн', 'инн клиента', 'налоговый номер', 'tax id', 'taxid'],
  contactPerson: [
    'контакт',
    'контактное лицо',
    'фио',
    'имя',
    'лпр',
    'contact',
    'contact person',
  ],
  phone: ['телефон', 'тел', 'мобильный', 'номер телефона', 'phone', 'mobile'],
  email: ['email', 'e-mail', 'почта', 'электронная почта', 'mail'],
  city: ['город', 'населенный пункт', 'регион', 'city', 'location'],
  segment: [
    'сегмент',
    'тип клиента',
    'категория',
    'отрасль',
    'класс спроса',
    'segment',
    'category',
  ],
  potential: [
    'потенциал',
    'оборот',
    'сумма',
    'выручка',
    'объем',
    'объём',
    'предел суммы кредита',
    'лимит кредита',
    'potential',
    'revenue',
    'amount',
  ],
  notes: [
    'комментарий',
    'примечание',
    'заметка',
    'описание',
    'досье клиента',
    'notes',
    'comment',
  ],
}

function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase('ru')
    .replace(/[._/\\-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

export function autoMapHeaders(headers: string[]): ImportMapping {
  const normalized = headers.map(normalizeHeader)
  const mapping = Object.fromEntries(
    IMPORT_FIELDS.map(({ value }) => [value, null]),
  ) as ImportMapping

  for (const field of IMPORT_FIELDS) {
    const index = normalized.findIndex((header) =>
      SYNONYMS[field.value].some(
        (synonym) => header === synonym || header.includes(synonym),
      ),
    )
    mapping[field.value] = index >= 0 ? index : null
  }

  return mapping
}

function parseCsvLine(line: string, delimiter: string): string[] {
  const values: string[] = []
  let value = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    const next = line[index + 1]

    if (char === '"' && quoted && next === '"') {
      value += '"'
      index += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === delimiter && !quoted) {
      values.push(value.trim())
      value = ''
    } else {
      value += char
    }
  }

  values.push(value.trim())
  return values
}

export function parseCsv(text: string): unknown[][] {
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n')
  const lines = normalized.split('\n').filter((line) => line.trim())
  if (lines.length > MAX_IMPORT_ROWS + 1) {
    throw new Error(`В файле больше ${MAX_IMPORT_ROWS.toLocaleString('ru-RU')} строк.`)
  }
  const firstLine = lines[0] ?? ''
  const delimiter =
    (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0)
      ? ';'
      : ','

  return lines.map((line) => parseCsvLine(line, delimiter))
}

function validateTable(table: unknown[][]): void {
  if (table.length > MAX_IMPORT_ROWS + 1) {
    throw new Error(`В файле больше ${MAX_IMPORT_ROWS.toLocaleString('ru-RU')} строк.`)
  }
  if (table.some((row) => row.length > MAX_IMPORT_COLUMNS)) {
    throw new Error(`В файле больше ${MAX_IMPORT_COLUMNS} колонок.`)
  }
  if (
    table.some((row) =>
      row.some((value) => String(value ?? '').length > MAX_CELL_LENGTH),
    )
  ) {
    throw new Error('В файле есть слишком длинная ячейка.')
  }
}

function countMappedHeaders(row: unknown[]): number {
  const headers = row.map((value) => String(value ?? '').trim())
  const mapping = autoMapHeaders(headers)
  return Object.values(mapping).filter((index) => index !== null).length
}

function findHeaderRow(table: unknown[][]): number {
  let bestIndex = 0
  let bestScore = -1

  table.slice(0, 25).forEach((row, index) => {
    const score = countMappedHeaders(row)
    if (score > bestScore) {
      bestIndex = index
      bestScore = score
    }
  })

  return bestScore >= 2 ? bestIndex : 0
}

function isDprClientReport(headers: string[]): boolean {
  const normalized = headers.map(normalizeHeader)
  return ['инн', 'кпп', 'номер соглашения'].some((header) =>
    normalized.includes(header),
  )
}

function formatDossier(headers: string[], row: unknown[]): string {
  const wanted = new Set([
    'Код',
    'ИНН',
    'КПП',
    'Класс профиля',
    'Условия платежа',
    'Предел суммы кредита',
    'Предел суммы кредита заказа',
    'Номер соглашения',
    'Наименование соглашения',
    'Дата начала договора',
    'Дата окончания договора',
    'Условия платежа по договору',
    'Прейскурант',
    'Стандартный процент скидки',
    'Почтовый индекс',
    'Область',
    'Адрес',
  ])

  return headers
    .map((header, index) => {
      if (!wanted.has(header)) return ''
      const value = valueAt(row, index)
      return value ? `${header}: ${value}` : ''
    })
    .filter(Boolean)
    .join('\n')
}

export async function readClientFile(file: File): Promise<ParsedTable> {
  const extension = file.name.split('.').pop()?.toLocaleLowerCase('ru')
  if (!extension || !['csv', 'xlsx'].includes(extension)) {
    throw new Error('Поддерживаются только файлы XLSX и CSV.')
  }
  if (file.size > MAX_IMPORT_BYTES) {
    throw new Error('Файл слишком большой. Максимальный размер — 10 МБ.')
  }
  if (extension === 'xlsx') {
    const workbook = (await readWorkbook(file)) as WorkbookSheet[]
    const totalRows = workbook.reduce((sum, sheet) => sum + sheet.data.length, 0)
    if (totalRows > MAX_IMPORT_ROWS) {
      throw new Error(`В книге больше ${MAX_IMPORT_ROWS.toLocaleString('ru-RU')} строк.`)
    }
    workbook.forEach((sheet) => validateTable(sheet.data))

    if (isOsintWorkbook(workbook)) {
      const osintRecords = parseOsintSheets(workbook, file.name)
      return {
        filename: file.name,
        kind: 'osint',
        headers: ['Клиент', 'ИНН', 'Тип', 'Сегмент', 'Категория выручки', 'Статус'],
        rows: osintRecords.map((record) => [
          record.company,
          record.taxId,
          record.osint.legalType,
          record.osint.segment,
          record.osint.revenueCategory,
          record.osint.companyStatus,
        ]),
        osintRecords,
      }
    }
  }

  const table: unknown[][] =
    extension === 'csv'
      ? parseCsv(await file.text())
      : (await readSheet(file, 1)) as unknown[][]

  validateTable(table)

  if (table.length < 2) {
    throw new Error('В файле должна быть строка заголовков и хотя бы один клиент.')
  }

  const headerRowIndex = findHeaderRow(table)
  let headers = table[headerRowIndex].map((value, index) =>
    String(value ?? '').trim() || `Колонка ${index + 1}`,
  )
  let rows = table
    .slice(headerRowIndex + 1)
    .filter((row) => row.some((value) => String(value ?? '').trim()))

  if (isDprClientReport(headers)) {
    rows = rows.map((row) => [...row, formatDossier(headers, row)])
    headers = [...headers, 'Досье клиента']
  }

  return {
    filename: file.name,
    kind: 'standard',
    headers,
    rows,
  }
}

function valueAt(row: unknown[], index: number | null): string {
  if (index === null || index < 0) return ''
  const value = row[index]
  if (value instanceof Date) return value.toISOString()
  return String(value ?? '').trim()
}

export function parsePotential(value: string): number | null {
  if (!value.trim()) return null
  const cleaned = value
    .replace(/\s|\u00a0/g, '')
    .replace(/₽|руб\.?|р\.?/gi, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '')
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

function dedupeKey(client: Pick<Client, 'company' | 'phone' | 'email' | 'taxId'>): string {
  const taxId = normalizeTaxId(client.taxId)
  if (taxId) return `inn:${taxId}`
  const company = client.company.trim().toLocaleLowerCase('ru')
  const phone = client.phone.replace(/\D/g, '')
  const email = client.email.trim().toLocaleLowerCase('ru')
  return [company, phone, email].join('|')
}

export function rowsToClients(
  table: ParsedTable,
  mapping: ImportMapping,
  existingClients: Client[],
): { clients: Client[]; skipped: number } {
  if (mapping.company === null) {
    throw new Error('Укажите колонку с названием компании.')
  }

  const existingKeys = new Set(existingClients.map(dedupeKey))
  const imported: Client[] = []
  let skipped = 0

  for (const row of table.rows) {
    const now = new Date().toISOString()
    const client: Client = {
      id: crypto.randomUUID(),
      company: valueAt(row, mapping.company),
      taxId: normalizeTaxId(valueAt(row, mapping.taxId)),
      contactPerson: valueAt(row, mapping.contactPerson),
      phone: valueAt(row, mapping.phone),
      email: valueAt(row, mapping.email),
      city: valueAt(row, mapping.city),
      segment: valueAt(row, mapping.segment),
      potential: parsePotential(valueAt(row, mapping.potential)),
      status: 'new',
      important: false,
      attempts: 0,
      lastContactAt: null,
      nextContactAt: null,
      result: '',
      notes: valueAt(row, mapping.notes),
      interactions: [],
      createdAt: now,
      updatedAt: now,
    }

    const key = dedupeKey(client)
    if (!client.company || existingKeys.has(key)) {
      skipped += 1
      continue
    }

    existingKeys.add(key)
    imported.push(client)
  }

  return { clients: imported, skipped }
}
